import { ThemeProvider } from "styled-components";
import { lightenColor } from "./theme-utils";

// global tokens
export const radii = [0, 4, 8];
export const fonts = {
	normal: "Roboto, sans-serif",
};

//TODO leave only hover and default
export const shadows: any = [
	"0 10px 10px 0 rgba(219, 219, 219, 0.5)",
	"0 2px 9px 0 rgba(15, 156, 246, 0.5)",
	"0 3px 25px 0 rgba(218, 218, 218, 0.37)",
	"0 3px 26px 0 rgba(36, 160, 244, 0.47)",
	"0 0 9px 0 rgba(227, 6, 19, 0.5)",
	"0px 3px 25px rgba(103, 103, 103, 0.37)",
	"0 0 9px 0 rgba(238, 149, 4, 0.5)",
];
export const breakpoints: any = ["640px", "1024px", "1200px"];
export const fontSizes: any = [12, 14, 16, 18, 20, 24, 28, 32, 36];
export const fontWeights: any = [300, 400, 500, 700];
export const space: any = [0, 4, 8, 12, 16, 20, 24, 28, 32, 36];
export const opacity: any = [0, 0.25, 0.5, 0.75, 1];
export const sizes: any = [];
export const borderWidths: any = [];

borderWidths["border-size-thin"] = "1px";
borderWidths["border-size-thick"] = "2px";
borderWidths["border-size-bold"] = "6px";

// Font size aliases
fontSizes.h1 = fontSizes[8];
fontSizes.h2 = fontSizes[7];
fontSizes.h3 = fontSizes[6];
fontSizes.h4 = fontSizes[5];
fontSizes.h5 = fontSizes[4];
fontSizes.h6 = fontSizes[2];
fontSizes["body-small"] = fontSizes[1];
fontSizes.body = fontSizes[2];
fontSizes["body-medium"] = fontSizes[3];
fontSizes["body-large"] = fontSizes[4];
fontSizes.label = fontSizes[0];
fontSizes.caption = fontSizes[0];
fontSizes["body-lg"] = fontSizes[3];

// Font weights aliases
fontWeights.light = fontWeights[0];
fontWeights.normal = fontWeights[1];
// the design team uses "medium" as a word rather than semibold
// we're keeping semibold for legacy reasons
fontWeights.semiBold = fontWeights[2];
fontWeights.medium = fontWeights[2];
fontWeights.bold = fontWeights[3];

// Color aliases

// Shadow aliases
shadows.default = shadows[2];
shadows.hover = shadows[5];
shadows.accent = shadows[1];
shadows.error = shadows[4];
shadows.warning = shadows[6];

// Breakpoints  aliases
breakpoints.mobile = "0px";
breakpoints.tablet = "415px";
breakpoints.desktop = "769px";
breakpoints.header = "760px";

// Grid aliases
sizes.maxGridWidth = "1440px";
space.mobileGridMargin = "20px";
space.desktopGridMargin = "60px";

type ThemeProviderProps = { theme: any; children: any };

export const BCAThemeProvider = ({ theme, children }: ThemeProviderProps) => (
	<ThemeProvider theme={theme}>{children}</ThemeProvider>
);

// BCA color pallete, subject to be rewritten soon
const colors: any = {
	"primary-navy-blue": "#152d6d",
	"primary-white": "#ffffff",
	"primary-red": "#e30613",
	"primary-light-blue": "#2196f3",
	"secondary-purple": "#7a5da4",
	"secondary-orange": "#ee9504",
	"secondary-nav-blue": "#091535",
	"secondary-teal": "#0076a7",
	"secondary-green": "#609833",
	"secondary-yellow": "#fdca4d",
	"secondary-light-grey": "#f6f6f6",
	"secondary-darkest-grey": "#262726",
	"footer-blue": "#070b16",
	"grey-blue": "#374D62",
	"lightest-grey": "#bebfbe",
	"msg-blue-border": "#a1c2da",
	"msg-light-blue": "#e9f5fe",
	black: "#000000",
	"gradient-primary": "linear-gradient(rgba(33 201 243), rgba(33 150 243))",
	"hero-gradient": "linear-gradient(#091535, #0C1223)",
	"blue-hover": "#126cb4",
};

//New Colors
colors["grey-lighten-95"] = lightenColor(colors["grey-blue"], 95);
colors["grey-lighten-85"] = lightenColor(colors["grey-blue"], 85);
colors["grey-lighten-50"] = lightenColor(colors["grey-blue"], 50);
colors["orange-lighten-96"] = lightenColor(colors["secondary-orange"], 96);
colors["red-lighten-97"] = lightenColor(colors["primary-red"], 97);
colors["green-lighten-94"] = lightenColor(colors["secondary-green"], 94);
colors["green-lighten-96"] = lightenColor(colors["secondary-green"], 96);
colors["blue-lighten-94"] = lightenColor(colors["primary-light-blue"], 94);

// Currently used color aliases, these are to become legacy please not reuse
colors.primary = colors["primary-light-blue"];
colors.secondary = colors["secondary-purple"];
colors["secondary-dark"] = colors["secondary-dark-blue"];

// New aliases
colors["text-color"] = colors["footer-blue"];
colors["lighter-text-color"] = colors["grey-blue"];
colors["text-color-over-background"] = colors["primary-white"];
colors["text-color-hover"] = colors["primary-light-blue"];
colors["informative-text-color"] = colors["grey-blue"];
colors["positive-text-color"] = colors["secondary-green"];
colors["negative-text-color"] = colors["primary-red"];
colors["warning-text-color"] = colors["secondary-orange"];

// PRIMARY CTA
// backgrounds
colors["cta-primary-background-color-default"] = colors["primary-light-blue"];
colors["cta-primary-background-color-hover"] = colors["blue-hover"];
colors["cta-primary-background-color-focus"] = colors["blue-hover"];
// text
colors["cta-primary-text-color-default"] = colors["primary-white"];
colors["cta-primary-text-color-hover"] = colors["primary-white"];
colors["cta-primary-text-color-focus"] = colors["primary-white"];

// SECONDARY CTA
// text
colors["cta-secondary-text-color-default"] = colors["primary"];
colors["cta-secondary-text-color-hover"] = colors["blue-hover"];
colors["cta-secondary-text-color-focus"] = colors["blue-hover"];

// borders
colors["cta-secondary-border-color-default"] = colors["primary"];
colors["cta-secondary-border-color-hover"] = colors["blue-hover"];
colors["cta-secondary-border-color-focus"] = colors["blue-hover"];

// TERTIARY CTA
// text
colors["cta-tertiary-text-color-default"] = colors["primary"];
colors["cta-tertiary-text-color-hover"] = colors["blue-hover"];
colors["cta-tertiary-text-color-focus"] = colors["blue-hover"];

// BUTTON CTA
// text
colors["cta-button-text-color-default"] = colors["footer-blue"];
colors["cta-button-text-color-hover"] = colors["primary-light-blue"];
colors["cta-button-text-color-focus"] = colors["text-color-over-background"];

// border
colors["cta-button-border-color-hover"] = colors["primary-light-blue"];
// background
colors["cta-button-background-color-active"] = colors["primary-light-blue"];

// Checkbox
colors["checkbox-fill-color"] = colors["primary-light-blue"];
colors["checkbox-bg-color"] = colors["primary-white"];
colors["checkbox-font-color"] = colors["grey-blue"];

// Radio
colors["radio-fill-color"] = colors["primary-light-blue"];

colors["background-default-color"] = colors["primary-white"];
colors["background-dark-color"] = colors["secondary-nav-blue"];
colors["informative-background-color"] = colors["msg-light-blue"];

colors["informative-border-color"] = colors["msg-blue-border"];

colors["outline-color"] = colors["primary-light-blue"];

colors["divider-color"] = "#e8e8e8";

colors["progress-bar-fill-color"] = colors["primary-light-blue"];

colors["stepper-active-color"] = colors["grey-blue"];
colors["stepper-inactive-color"] = "#E5E5E5";

//Badges
colors["neutral-background-color"] = colors["grey-blue"];
colors["positive-background-color"] = colors["secondary-green"];
colors["negative-background-color"] = colors["primary-red"];
colors["warning-background-color"] = colors["secondary-orange"];

//CHIP
colors["chip-read-only-border-color"] = colors["msg-blue-border"];

//COUNT
colors["count-active-background-color"] = "rgba(33, 150, 243, 0.1)"; // cag1 with 0.1 opacity

//FAQAccordion
colors["accordion-default-header-color"] = colors["black"];

// Circular Loader
colors["circular-loader-background"] = colors["lightest-grey"];
colors["circular-loader-accent-color"] = colors["primary-light-blue"];

// Backdrop
colors["primary-backdrop"] = colors["primary-navy-blue"];

//LabelNumber
colors["label-number-background-color"] = colors["msg-blue-border"];

//Banner
colors["banner-informative-color"] = colors["primary-light-blue"];
colors["banner-informative-background-color"] = colors["blue-lighten-94"];
colors["banner-positive-background-color"] = colors["green-lighten-94"];
colors["banner-negative-background-color"] = colors["red-lighten-97"];
colors["banner-warning-background-color"] = colors["orange-lighten-96"];

//Table
colors["table-head-bg-color"] = colors["grey-lighten-95"];
colors["table-body-font-color"] = colors["grey-blue"];
colors["table-head-font-color"] = colors["footer-blue"];
colors["table-border-color"] = colors["grey-lighten-85"];
colors["table-bg-color"] = colors["primary-white"];
colors["table-row-highlight-color"] = colors["primary-red"];

//Vertical Timeline
colors["task-default-background-color"] = lightenColor(
	colors["informative-background-color"],
	84
);
colors["task-success-background-color"] = colors["green-lighten-96"];

//Toggle
colors["toggle-bg-color"] = "#F5F6F7";
colors["toggle-default-bg-color"] = colors["primary-white"];
colors["toggle-default-color"] = colors["grey-blue"];
colors["toggle-info-bg-color"] = colors["primary-light-blue"];
colors["toggle-info-color"] = colors["primary-white"];
colors["toggle-inactive-option-color"] = colors["grey-blue"];

//Snackbar
colors["snackbar-progress-bg"] = "rgba(21, 45, 109, 0.28)";
colors["snackbar-informative-color"] = "#f6fbff";
colors["snackbar-secondary-color"] = "#f6f7f9";
colors["snackbar-negative-color"] = "#fef8f8";
colors["snackbar-warning-color"] = "#fefbf5";
colors["snackbar-positive-color"] = "#f7fbf7";

export const defaultTheme = {
	colors,
	radii,
	fonts,
	fontSizes,
	opacity,
	lineHeights: [1.4],
	letterSpacings: [0.7],
	fontWeights,
	space,
	shadows,
	sizes,
	breakpoints,
	borders: {
		normalBorder: "1px solid #f1f1f1",
		errorBorder: `1px solid ${colors["primary-red"]}`,
		primaryBorder: `1px solid ${colors["primary-light-blue"]}`,
		warningBorder: `1px solid ${colors["secondary-orange"]}`,
	},
	borderWidths,
	name: "BCA",
};
